import React, { useState, useEffect, useRef } from "react";
import { Player } from "@lottiefiles/react-lottie-player";
import { GLOBAL_IMAGES, SIDEBAR } from "../../assets/asset";
import QuestionSidebar from "../QuestionSidebar/QuestionSidebar";
import Whiteboard from "../QuestionSidebar/Whiteboard";
import ExplanationModal from "../ExplanationModal";
import "../../App.css";
import CustomButton from "../../assets/buttons/CustomButton";
import animationData from "../../utils/happy_dog_animation.json";
import { useVideoModal } from "../../hooks/useVideoModal";
import { useAudioManager } from "../../hooks/useAudioManager";

const Checkboxtype = ({
  questionData,
  onCorrectAnswer,
  video,
  colorTheme = "#ccc",
  questionKey,
  yearId,
}) => {
  const { isVideoModalOpen, handleVideoModalToggle } = useVideoModal();
  const {
    question,
    questionimagelist,
    answer,
    questionaudio,
    explaination
  } = questionData;

  // Move useAudioManager call after the state that causes re-renders
  const [selectedType, setSelectedType] = useState(null);
  const [userInput, setUserInput] = useState("");
  const [isCorrect, setIsCorrect] = useState(null);
  const [isAttempt, setIsAttempt] = useState(null);
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showWhiteboard, setShowWhiteboard] = useState(false);
  const [isDelayed, setIsDelayed] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [totalHeight, setTotalHeight] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [audioInitialized, setAudioInitialized] = useState(false); // Add this flag

  // Only call useAudioManager after initial state is set
  const {
    handleAudioReplay,
    playFeedbackAudio,
    stopAllAudio
  } = useAudioManager(
    audioInitialized ? questionaudio : null, // Only pass audio after initialization
    questionKey
  );
  // on video play stop all audios
  useEffect(
    () => {
      if (isVideoModalOpen) {
        stopAllAudio();
      }
    },
    [isVideoModalOpen, stopAllAudio]
  );

  const questionDivRef = useRef(null);
  const mainContentDivRef = useRef(null);
  const bottomButtonsDivRef = useRef(null);
  const totalViewRef = useRef(null);

  const colorWithOpacity = (color, opacity) => {
    const [r, g, b] = color.match(/\w\w/g).map(x => parseInt(x, 16));
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const backgroundColor = colorWithOpacity(colorTheme, 1);

  // Combine state reset and audio initialization in one useEffect
  useEffect(
    () => {
      setIsCorrect(null);
      setIsAttempt(null);
      setSelectedType(null);
      setUserInput("");
      setWrongAttempts(0);
      setStartTime(new Date());
      setIsProcessing(false);

      // Initialize audio after state is reset (with a small delay to prevent multiple calls)
      const timer = setTimeout(() => {
        setAudioInitialized(true);
      }, 50);

      return () => {
        clearTimeout(timer);
        setAudioInitialized(false);
      };
    },
    [questionKey]
  );

  const handleWhiteboardToggle = () => setShowWhiteboard(!showWhiteboard);

  const handleCheckboxChange = event => setSelectedType(event.target.value);
  const handleInputChange = event => setUserInput(event.target.value);

  const resetToUnattempted = () => {
    setIsCorrect(null);
    setIsAttempt(null);
    setSelectedType(null);
    setUserInput("");
  };

  const handleCorrectIncorrect = () => {
    if (isProcessing) return;

    setIsProcessing(true);

    const data = (userInput.toUpperCase() + selectedType.toUpperCase()).trim();

    if (answer[0] === data) {
      setIsCorrect(true);
      playFeedbackAudio(true);
      setIsDelayed(true);
      setTimeout(() => {
        const endTime = new Date();
        const timeTaken = (endTime - startTime) / 1000;
        onCorrectAnswer(true, wrongAttempts + 1, timeTaken);
        resetToUnattempted();
        setIsDelayed(false);
        setIsProcessing(false);
      }, 4000);
    } else {
      setIsCorrect(false);
      playFeedbackAudio(false);
      setWrongAttempts(prev => {
        const newWrongAttempts = prev + 1;
        if (newWrongAttempts === 3) {
          setIsModalOpen(true);
          setTimeout(() => {
            setWrongAttempts(0);
          }, 0);
        }
        return newWrongAttempts;
      });

      setTimeout(() => {
        resetToUnattempted();
        setIsProcessing(false);
      }, 4000);
    }
  };

  // Optimize height calculation to prevent re-renders
  useEffect(
    () => {
      // Only calculate height once after component is fully rendered
      const timer = setTimeout(() => {
        if (
          questionDivRef.current &&
          mainContentDivRef.current &&
          bottomButtonsDivRef.current &&
          totalViewRef.current &&
          totalHeight === 0 // Only calculate if not already calculated
        ) {
          const questionDivHeight = questionDivRef.current.offsetHeight;
          const mainContentDivHeight = mainContentDivRef.current.offsetHeight;
          const bottomButtonsDivHeight =
            bottomButtonsDivRef.current.offsetHeight;
          const totalHeightInPx =
            questionDivHeight + mainContentDivHeight + bottomButtonsDivHeight;
          setTotalHeight(totalHeightInPx);
        }
      }, 100); // Increased delay to ensure DOM is ready

      return () => clearTimeout(timer);
    },
    [questionKey]
  ); // Only depend on questionKey, not other state variables

  return (
    <div
      className={`w-[92vw] h-[78vh] relative ${totalHeight <= 640
        ? "mt-[-4rem]"
        : ""}`}
      ref={totalViewRef}
    >
      {showWhiteboard &&
        !isVideoModalOpen &&
        <div className="absolute right-0 z-10">
          <Whiteboard
            style={{ height: totalHeight }}
            onClose={handleWhiteboardToggle}
          />
        </div>}
      <div
        className="flex items-center bg-opacity-75 py-4 sm:py-6 md:py-8 lg:py-10 rounded-t-[30px] shadow-lg border-2 border-[#cccccc] border-b-0"
        style={{ backgroundColor }}
        ref={questionDivRef}
      >
        <img
          src={SIDEBAR.audio}
          onClick={handleAudioReplay}
          alt="Audio"
          className="h-8 w-8 mx-6 sm:mx-8 md:mx-10 flex-shrink-0"
        />
        <div className={`text-lg sm:text-xl md:text-[22px] ${Number(yearId) >= 5 ? "text-white" : "text-black"} font-bold font-mali`}>
          {question}
        </div>
      </div>

      <div
        ref={mainContentDivRef}
        className={`bg-black bg-opacity-30 ${!isVideoModalOpen
          ? "backdrop-blur-[2px]"
          : ""} flex flex-col justify-center items-center py-4 relative h-[70%] z-0 border-2 border-[#cccccc] border-t-0 border-b-0`}
      >
        <div className="absolute left-0 top-[20%]">
          <QuestionSidebar
            onAudioPlay={handleAudioReplay}
            videoUrl={video}
            SIDEBAR={SIDEBAR}
            onVideoModalToggle={handleVideoModalToggle}
            onWhiteboardToggle={handleWhiteboardToggle}
          />
        </div>
        <div className="flex flex-col justify-center items-center w-[90%] sm:w-[60%] md:w-[50%] lg:w-[30%] h-full">
          {questionimagelist.length > 0 &&
            questionimagelist.map((item, index) =>
              <img
                key={index}
                src={item}
                alt={`Question ${index}`}
                className="w-auto max-h-[200px] sm:max-h-[220px] md:max-h-[250px] object-contain"
              />
            )}
        </div>
        <div className="flex flex-col items-center justify-center w-[30vw]">
          <div className="bg-[#fafafa] py-[0.3rem] px-[1rem] rounded-lg gap-[0.10rem] w-full flex flex-col ">
            <span className="text-lg font-mulish">Type answer here</span>
            <input
              type="text"
              className="rounded-lg border border-black font-mulish p-2 w-full text-center"
              placeholder="Enter Your Answer"
              value={userInput}
              onChange={handleInputChange}
            />

            <div className="flex flex-col p-[10px] font-mulish gap-2 bg-[#CCCCCC] border rounded-md mt-[8px] w-full">
              <span className="text-lg">Select type</span>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="numberType"
                  value="Even"
                  checked={selectedType === "Even"}
                  onChange={handleCheckboxChange}
                />
                <span className="ml-2 text-sm sm:text-base">Even</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="numberType"
                  value="Odd"
                  checked={selectedType === "Odd"}
                  onChange={handleCheckboxChange}
                />
                <span className="ml-2 text-sm sm:text-base">Odd</span>
              </label>
            </div>
          </div>
        </div>
      </div>
      {!isVideoModalOpen &&
        <div
          ref={bottomButtonsDivRef}
          className="bg-white bg-opacity-75 rounded-b-[30px] flex items-center justify-between shadow-lg py-2 px-10 sm:px-12 md:px-16 lg:px-20 border-2 border-[#cccccc] border-t-0"
          style={{ backgroundColor }}
        >
          <div className="flex justify-center items-center">
            <div className="flex gap-[5px] items-center">
              <Player
                autoplay
                loop
                src={animationData}
                style={{ height: "120px", width: "120px" }}
                className="sm:w-[100px] sm:h-[100px] md:w-[120px] md:h-[120px]"
              />
            </div>

            {isCorrect !== null &&
              <CustomButton
                text={isCorrect ? "Yaay! you did it" : "Oops! try again"}
                onClick={null}
                disabled={true}
                type={isCorrect ? "correct" : "incorrect"}
              />}
          </div>
          <CustomButton
            text={isCorrect === false ? "Try Again" : "Click to continue"}
            onClick={handleCorrectIncorrect}
            isShaking={isCorrect === false}
            disabled={
              userInput === "" ||
              selectedType == null ||
              isDelayed ||
              isProcessing
            }
            type={
              isCorrect === true
                ? "correct"
                : isCorrect === false
                  ? "incorrect"
                  : selectedType !== null ? "selected" : "disabled"
            }
          />
        </div>}
      {isModalOpen &&
        <ExplanationModal
          questionData={questionData}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          explanation={explaination}
        />}
    </div>
  );
};

export default Checkboxtype;
